import { PublicClientApplication } from '@azure/msal-browser';
import { getId } from '@/assets/js/utils';
import { authorityConstant } from '@/assets/js/authorityConstant';
import router from '@/router';
import { xhr } from '@/xhr/endpoints';
import jwt_decode from 'jwt-decode';

//
// state
const state = {
  /** Azure Authentication */
  msalInstance: null,
  authInitialized: false,
  loadingMyAccount: false, // @TODO implement usage with feedback (loader?)
  publicView: false,
  publicLinkDocId: null,
  publicLinkDebateId: null,
  visitor: null,
  msalConfig: {
    auth: {
      clientId: '262d39db-b6ac-4a90-94de-3a864f450285',
      authority: `https://${authorityConstant}/PersuastionStudiesB2C.onmicrosoft.com/B2C_1_SignUp_SignIn`,
      // New >>>
      // authority: `https://${authorityConstant}/PersuastionStudiesB2C.onmicrosoft.com/v2.0/.well-known/openid-configuration?p=B2C_1_SignUp_SignIn`,
      redirectUri: `${document.location.origin}/`,
      postLogoutRedirectUri: `${document.location.origin}/`,
      mainWindowRedirectUri: `${document.location.origin}/`,
      knownAuthorities: [
        `https://${authorityConstant}/PersuastionStudiesB2C.onmicrosoft.com/v2.0/.well-known/openid-configuration?p=B2C_1_SignUp_SignIn`,
        //  New
        // `https://${authorityConstant}/PersuastionStudiesB2C.onmicrosoft.com/v2.0/.well-known/openid-configuration?p=B2C_1_SignUp_SignIn`
      ],
      scopes: [
        'https://PersuastionStudiesB2C.onmicrosoft.com/262d39db-b6ac-4a90-94de-3a864f450285/BackendApi',
      ],
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: true,
    },
  },
  localStorageClaimsKey: `${authorityConstant}-`,
  localStorageTokenKey: `${authorityConstant}-idtoken-`,
  idToken: null,
  idTokenClaims: null,
  impersonatorIdToken: null,
  lastPreImpersonationPath: null,

  /** UI */
  UI: {
    showSidebar: true,
    showCompactSidebar: false,
    showTopbar: true,
  },
};

//
// getters
const getters = {
  publicLinkID: state => getId(state.publicLinkData),
};

//
// mutations
const mutations = {
  /** Azure Authentication */
  setMsalInstance(state, msalInstance) {
    state.msalInstance = msalInstance;
  },
  setAuthInitialized(state, authInitialized) {
    state.authInitialized = authInitialized;
  },
  setIdToken(state, token) {
    state.idToken = token;
    try {
      state.idTokenClaims = jwt_decode(state.idToken);
    } catch {
      state.idTokenClaims = null;
    }
  },
  setIdTokenFromStorage(state, idToken) {
    state.idToken = idToken;
  },
  setClaimsFromStorage(state, idTokenClaims) {
    state.idTokenClaims = idTokenClaims;
  },
  setImpersonatorIdToken(state, token) {
    state.impersonatorIdToken = token;
  },
  setLastPreImpersonationLocation(state, path) {
    state.lastPreImpersonationPath = path;
  },

  setLoadingMyAccount(state, value) {
    state.loadingMyAccount = value;
  },

  /** UI */
  showSidebar: state => {
    state.UI.showSidebar = true;
    state.UI.showCompactSidebar = false;
  },
  hideSidebar: state => {
    state.UI.showSidebar = false;
    state.UI.showCompactSidebar = true;
  },
  hideTopbar: state => {
    state.UI.showTopbar = false;
  },
  showTopbar: state => {
    state.UI.showTopbar = true;
  },

  /** Public views ### */
  setPublicView: (state, val) => (state.publicView = val),
  setPublicLinkDocId: (state, val) => (state.publicLinkDocId = val),
  setPublicLinkDebateId: (state, val) => (state.publicLinkDebateId = val),
  setVisitor: (state, val) => (state.visitor = val),
};

async function tryFetchAndSetToken(context, msal) {
  try {
    let tokenRequestResult = await msal.acquireTokenSilent({ ...context.state.msalConfig.auth });
    if (tokenRequestResult) {
      context.commit('setIdToken', tokenRequestResult.idToken);
    } else {
      msal.setActiveAccount();
    }
  } catch (err) {
    msal.setActiveAccount();
  }
}

//
// actions
const actions = {
  /**
   * Azure Authentication
   **/
  InitializeAuth: async context => {
    const msal = new PublicClientApplication(context.state.msalConfig);
    context.commit('setMsalInstance', msal);
    const activeAccount = msal.getActiveAccount();
    if (activeAccount) {
      if (
        !activeAccount.idTokenClaims ||
        !activeAccount.idTokenClaims.exp ||
        new Date() > new Date(activeAccount.idTokenClaims.exp * 1000)
      ) {
        msal.setActiveAccount();
      } else {
        let msalStorage = msal.browserStorage.browserStorage.windowStorage;
        if (msalStorage.length) {
          for (const key in msalStorage) {
            if (key.includes('idtoken')) {
              await context.commit('setIdToken', JSON.parse(msalStorage[key]).secret);
              context.commit('setLoadingMyAccount', true);
              await context.dispatch('loadMyAccount');
              context.commit('setLoadingMyAccount', false);
              await context.dispatch('getNotifications');
            }
          }
        }
      }
    } else {
      const allAccounts = msal
        .getAllAccounts()
        .sort(x => x.idTokenClaims.exp)
        .reverse();
      if (
        allAccounts.length > 0 &&
        new Date() < new Date(allAccounts[0].idTokenClaims.exp * 1000)
      ) {
        await tryFetchAndSetToken(context, msal);
        context.commit('setLoadingMyAccount', true);
        await context.dispatch('loadMyAccount');
        context.commit('setLoadingMyAccount', false);
        await context.dispatch('getNotifications');
      }
    }
    // if(!context.state.sessionId) {
    //    context.dispatch('createSession');
    // }
    context.commit('setAuthInitialized', true);
  },

  SignIn: async context => {
    try {
      const authResult = await context.state.msalInstance.loginPopup({
        scopes: context.state.msalConfig.auth.scopes,
      });
      if (authResult && authResult.account) {
        await context.state.msalInstance.setActiveAccount(authResult.account);
        await context.commit('setIdToken', authResult.idToken);
        await context.dispatch('loadMyAccount');
        await context.dispatch('getNotifications');
        // await context.dispatch('createSession');
      } else {
        context.state.msalInstance.setActiveAccount();
      }
      return true;
    } catch (err) {
      alert(`error during authentication: ${err}`);
      return false;
    }
  },

  SignOut: async context => {
    const msal = context.state.msalInstance;
    const allAccounts = msal
      ?.getAllAccounts()
      ?.sort(x => x.idTokenClaims.exp)
      ?.reverse();
    if (
      allAccounts?.length > 0 &&
      new Date() < new Date((allAccounts[0].idTokenClaims?.exp || 0) * 1000)
    ) {
      // filter on the account that you want to delete from the cache.
      // I take the first one here to keep the code sample short
      const account = allAccounts[0];
      //
      // logout from B2C session
      await msal.logoutRedirect({
        account,
      });
    }
    //
    // clear local session
    context.commit('setIdToken', null);
    // clear browser's local storage
    const b2cStorageKeys = Object.keys(window.localStorage).filter(k =>
      k.match(`${authorityConstant}-`)
    );
    const msalKeys = Object.keys(window.localStorage).filter(k => k.match(`msal`));
    [...b2cStorageKeys, ...msalKeys].forEach(k => window.localStorage.removeItem(k));
    router.push('/');
  },
  impersonateUser: async (context, { userId, path }) => {
    const currentToken = context.state.idToken;
    try {
      const newToken = await xhr.impersonateUser(userId, currentToken);
      context.commit('setImpersonatorIdToken', currentToken);
      context.commit('setLastPreImpersonationLocation', router.history.current.path);
      context.commit('setIdToken', newToken);
      await context.dispatch('loadMyAccount');
      router.push(path);
    } catch (err) {
      console.log(err);
      context.commit('setIdToken', currentToken);
      context.commit('setImpersonatorIdToken', null);
      context.commit('setLastPreImpersonationLocation', null);
    }
  },
  stopImpersonation: async context => {
    try {
      context.commit('setIdToken', context.state.impersonatorIdToken);
      await context.dispatch('loadMyAccount');
      context.commit('setImpersonatorIdToken', null);
      const path = context.state.lastPreImpersonationPath;
      context.commit('setLastPreImpersonationLocation', null);
      router.push(path);
    } catch (err) {
      context.commit('setIdToken', null);
      context.commit('setImpersonatorIdToken', null);
      context.commit('setLastPreImpersonationLocation', null);
    }
  },
};

//
// exports
export default {
  // namespaced: false,
  state,
  getters,
  actions,
  mutations,
};
